import { types } from 'mobx-state-tree';
import { BaseStore } from '@aws-ee/base-ui/dist/models/BaseStore';
import {
  getUserLearnings,
  getUserLearning,
  startUserLearningModule,
  activateUserLearningSubscription,
  getProjectAssignmentStatus,
  getOrCreateProjectAssignment,
  getProjectDownloadUrl,
  getPreSubmissionUploadUrl,
  confirmPreSubmissionUpload,
  getFinalSubmissionUploadUrl,
  confirmFinalSubmission,
  generateAchievementCertificate,
} from '../../helpers/api';
import { Learning } from './Learning';

// ==================================================================
// LearningStore
// ==================================================================
const LearningStore = BaseStore.named('LearningStore')
  .props({
    userLearnings: types.array(Learning),
  })
  .actions(self => {
    return {
      async doLoad() {
        const userLearnings = await getUserLearnings();
        self.runInAction(() => {
          self.userLearnings = userLearnings;
        });
      },

      async startUserLearningModule(userLearningId, moduleId, studyId) {
        const result = await startUserLearningModule(userLearningId, moduleId, studyId);
        const old = self.userLearnings.find(ul => ul.id === result.id);
        if (old) {
          self.runInAction(() => {
            old.set(result);
          });
          return old;
        }
        self.runInAction(() => {
          self.userLearnings.push(result);
        });
        return self.userLearnings.at(-1);
      },

      async getUserLearning(userLearningId) {
        const result = await getUserLearning(userLearningId);
        if (result.subscriptionRequired && !result.subscription?.activated) {
          let currentModulesCount = 0;
          for (let pIndex = 0; pIndex < result.paths.length; pIndex++) {
            const path = result.paths[pIndex];
            if (path.isCertificatePath) continue;
            for (let mIndex = 0; mIndex < path.modules.length; mIndex++) {
              if (!path.modules[mIndex].surveyId) currentModulesCount++;
              path.modules[mIndex].noActiveSubscriptionLimit =
                currentModulesCount > result.certificateProgram.enabledModulesCount;
              // The project cards' own buttons stay open to unpaid learners, so the limit locks only
              // their workspace launch, through a flag PathModules does not apply to the whole card.
              path.modules[mIndex].workspaceLocked = path.modules[mIndex].noActiveSubscriptionLimit;
            }
          }
        }

        const old = self.userLearnings.find(ul => ul.id === result.id);
        if (old) {
          self.runInAction(() => {
            old.set(result);
          });
          return old;
        }
        self.runInAction(() => {
          self.userLearnings.push(result);
        });
        return self.userLearnings.at(-1);
      },

      async activateUserLearningSubscription(userLearningId, code) {
        await activateUserLearningSubscription(userLearningId, { code });
      },

      async fetchProjectAssignment(userLearningId, pathId, moduleId) {
        return getOrCreateProjectAssignment(userLearningId, pathId, moduleId);
      },

      async fetchProjectAssignmentStatus(userLearningId) {
        return getProjectAssignmentStatus(userLearningId);
      },

      async downloadAssignedProject(userLearningId, pathId, moduleId) {
        const { url } = await getProjectDownloadUrl(userLearningId, pathId, moduleId);
        window.location.href = url;
      },

      async uploadPreSubmission(userLearningId, pathId, moduleId, file) {
        const { url } = await getPreSubmissionUploadUrl(userLearningId, pathId, moduleId);
        const putRes = await fetch(url, { method: 'PUT', body: file });
        if (!putRes.ok) throw new Error(`S3 upload failed (${putRes.status})`);
        const { userLearning } = await confirmPreSubmissionUpload(userLearningId, pathId, moduleId);
        const old = self.userLearnings.find(ul => ul.id === userLearning.id);
        if (old) {
          self.runInAction(() => {
            old.set(userLearning);
          });
        }
        return userLearning;
      },

      async uploadFinalSubmission(userLearningId, pathId, moduleId, file) {
        const { url, key } = await getFinalSubmissionUploadUrl(userLearningId, pathId, moduleId);
        const putRes = await fetch(url, { method: 'PUT', body: file });
        if (!putRes.ok) throw new Error(`S3 upload failed (${putRes.status})`);
        const result = await confirmFinalSubmission(userLearningId, pathId, moduleId, key);
        return result;
      },

      async issueAchievementCertificate(userLearningId) {
        return generateAchievementCertificate(userLearningId);
      },
    };
  })
  .views(() => ({}));

function registerContextItems(appContext) {
  appContext.learningStore = LearningStore.create({}, appContext);
}

export { LearningStore, registerContextItems };
